import React, { useEffect, useState } from "react";
import {  changePassword, fetchCompany, updateUserPassword } from "../services/apiService";
import { useNavigate } from "react-router-dom";
import { getToken, getUserFromToken } from "../services/authService";
import LoadingSpinner from "../components/LoadingSpinner";

 const ProfilePage = () => {
  const [userInfo, setUserInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [message, setMessage] = useState({ type: "", text: "" });
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const navigate = useNavigate();
  const [companies, SetCompany] = useState([]);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const token = localStorage.getItem("authToken");
        if (!token) { navigate("/login"); return; }
        setUserInfo(getToken());
      } catch (err) {
        setMessage({ type: "error", text: "Failed to load profile." });
      } finally {
        setLoading(false);
      }
    };
    loadCompany();
    fetchProfile();
  }, [navigate]);

  const loadCompany = async () => {
    try {
      const data = await fetchCompany();
      SetCompany(data);
    } catch (err) {
      console.error("Failed to fetch companies", err);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (!newPassword || !confirmPassword) {
      setMessage({ type: "error", text: "Please fill all fields." }); return;
    }
    if (newPassword !== confirmPassword) {
      setMessage({ type: "error", text: "New passwords do not match." }); return;
    }
    try {
      const username = getUserFromToken();
      const response = await changePassword(username, newPassword);
      if (response.success) {
        setMessage({ type: "success", text: "Password updated successfully." });
        setNewPassword(""); setConfirmPassword("");
      } else {
        setMessage({ type: "error", text: response.message || "Failed to update password." });
      }
    } catch (err) {
      setMessage({ type: "error", text: "An error occurred while updating password." });
    }
  };

  if (loading) return <LoadingSpinner label="Loading Profile" />;

  const company = companies.find(c => c.companyCode === userInfo?.companyCode)?.companyName || "N/A";

  return (
    <div className="min-h-screen py-10 px-4" style={{ backgroundColor: 'rgba(18,76,114,0.03)' }}>
      <div className="max-w-2xl mx-auto space-y-6">

        {/* Message */}
        {message.text && (
          <div className="px-4 py-3 rounded-xl text-sm font-medium flex items-center gap-2"
            style={{
              backgroundColor: message.type === "error" ? 'rgba(239,68,68,0.08)' : 'rgba(16,185,129,0.08)',
              color: message.type === "error" ? '#dc2626' : '#059669',
              border: `1px solid ${message.type === "error" ? 'rgba(239,68,68,0.2)' : 'rgba(16,185,129,0.2)'}`
            }}>
            {message.type === "error" ? "⚠️" : "✅"} {message.text}
          </div>
        )}

        {/* Profile Card */}
        <div className="bg-white rounded-2xl overflow-hidden"
          style={{ boxShadow: '0 8px 32px rgba(18,76,114,0.10)', border: '1px solid rgba(18,76,114,0.08)' }}>

          {/* Header Banner */}
          <div className="h-24 relative" style={{ background: 'linear-gradient(135deg, rgb(18,76,114), rgba(18,76,114,0.7))' }}>
            <div className="absolute -bottom-10 left-6">
              <img
                src={`https://ui-avatars.com/api/?name=${encodeURIComponent(userInfo?.unique_name || '')}&background=ffffff&color=124c72&bold=true&size=80`}
                alt="Avatar"
                className="w-20 h-20 rounded-full border-4 border-white shadow-lg"
              />
            </div>
          </div>

          {/* Profile Info */}
          <div className="pt-14 pb-6 px-6">
            <h2 className="text-xl font-bold mb-1" style={{ color: 'rgb(18,76,114)' }}>
              {userInfo?.unique_name || "N/A"}
            </h2>
            <p className="text-sm mb-6" style={{ color: 'rgba(18,76,114,0.6)' }}>{userInfo?.email}</p>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {[
                { label: "Role", value: userInfo?.role, icon: "🎭" },
                { label: "Company", value: company, icon: "🏢" },
                { label: "Department", value: userInfo?.department_name || "N/A", icon: "🏗️" },
              ].map((item) => (
                <div key={item.label} className="p-4 rounded-xl"
                  style={{ backgroundColor: 'rgba(18,76,114,0.04)', border: '1px solid rgba(18,76,114,0.08)' }}>
                  <p className="text-xs font-medium mb-1" style={{ color: 'rgba(18,76,114,0.5)' }}>
                    {item.icon} {item.label}
                  </p>
                  <p className="font-semibold text-sm truncate" style={{ color: 'rgb(18,76,114)' }}>
                    {item.value}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Change Password Card */}
        <div className="bg-white rounded-2xl p-6"
          style={{ boxShadow: '0 8px 32px rgba(18,76,114,0.10)', border: '1px solid rgba(18,76,114,0.08)' }}>

          <div className="flex items-center gap-3 mb-6 pb-4"
            style={{ borderBottom: '1px solid rgba(18,76,114,0.08)' }}>
            <div className="w-9 h-9 rounded-lg flex items-center justify-center"
              style={{ backgroundColor: 'rgba(18,76,114,0.08)' }}>
              <svg className="w-5 h-5" style={{ color: 'rgb(18,76,114)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                  d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
            <div>
              <h3 className="font-semibold" style={{ color: 'rgb(18,76,114)' }}>Change Password</h3>
              <p className="text-xs" style={{ color: 'rgba(18,76,114,0.5)' }}>Keep your account secure</p>
            </div>
          </div>

          <form onSubmit={handlePasswordChange} className="space-y-4">
            {[
              { label: "New Password", value: newPassword, setter: setNewPassword, show: showNew, toggle: () => setShowNew(p => !p) },
              { label: "Confirm New Password", value: confirmPassword, setter: setConfirmPassword, show: showConfirm, toggle: () => setShowConfirm(p => !p) },
            ].map((field) => (
              <div key={field.label}>
                <label className="block text-sm font-medium mb-1.5" style={{ color: 'rgb(18,76,114)' }}>
                  {field.label}
                </label>
                <div className="relative">
                  <input
                    type={field.show ? "text" : "password"}
                    value={field.value}
                    onChange={(e) => field.setter(e.target.value)}
                    className="w-full px-4 py-2.5 pr-10 rounded-xl outline-none text-sm text-gray-700 transition"
                    style={{ border: '1px solid rgba(18,76,114,0.25)', backgroundColor: 'white' }}
                    onFocus={e => e.target.style.boxShadow = '0 0 0 3px rgba(18,76,114,0.12)'}
                    onBlur={e => e.target.style.boxShadow = 'none'}
                    placeholder={`Enter ${field.label.toLowerCase()}`}
                  />
                  <button type="button" onClick={field.toggle}
                    className="absolute right-3 top-1/2 -translate-y-1/2"
                    style={{ color: 'rgba(18,76,114,0.5)' }}>
                    {field.show
                      ? <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" /></svg>
                      : <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                    }
                  </button>
                </div>
              </div>
            ))}

            <button type="submit"
              className="w-full py-2.5 rounded-xl text-white font-semibold text-sm transition-all mt-2"
              style={{ backgroundColor: 'rgb(18,76,114)', boxShadow: '0 4px 12px rgba(18,76,114,0.25)' }}
              onMouseEnter={e => e.currentTarget.style.backgroundColor = 'rgba(18,76,114,0.85)'}
              onMouseLeave={e => e.currentTarget.style.backgroundColor = 'rgb(18,76,114)'}>
              Update Password
            </button>
          </form>
        </div>

      </div>
    </div>
  );
};

export default ProfilePage;
 
